import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useExhibitor } from '../../hooks';
import { Exhibitor } from '../../dtos';
import { HomeLayout } from '../../components/layouts'
import { ExhibitorBalance, ExhibitorLogo, ExhibitorVouchersType, Header, TotalBalance } from './components';
import { Container, Carousel, Items, Item, ItemShortcut } from './styles';

const HomePage: React.FC = () => {
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const [selectedExhibitor, setSelectedExhibitor] = useState<Exhibitor>();
    const [itemWidth, setItemWidth] = useState<number>(0);

    const { exhibitors } = useExhibitor();

    const ref = useRef<HTMLDivElement | null>(null);

    const handleTouchMove = useCallback((event: TouchEvent) =>
        event.preventDefault(), []);

    const handleNextItem = useCallback((index: number) => {
        if (ref.current) {
            ref.current.scrollLeft = itemWidth * (index - 2) + 10;
            setSelectedIndex(index - 1);
            setSelectedExhibitor(exhibitors[index - 1]);
        }
    }, [itemWidth, exhibitors]);

    useEffect(() => {
        const element = ref.current;

        if (element && element.firstChild) {
            const style = window.getComputedStyle(element.firstChild as HTMLDivElement);
            const itemWidth = parseFloat(style.width) + parseFloat(style.marginLeft);
            let initialItemIndex = exhibitors.length > 2 ? Math.round(exhibitors.length / 2) : 1;

            setItemWidth(itemWidth);
            handleNextItem(initialItemIndex);

            element.addEventListener('touchmove', handleTouchMove, {
                passive: false
            });

            return () => element.removeEventListener('touchmove', handleTouchMove);
        }
    }, [handleNextItem, handleTouchMove, exhibitors.length]);

    const getAmountAvailable = useCallback((exhibitor: Exhibitor) =>
        exhibitor.voucherTypes.reduce<number>((amount, voucherType) =>
            amount + voucherType.amount, 0), []);

    const getTotalAmountAvailable = useCallback(() =>
        exhibitors.reduce<number>((amount, exhibitor) =>
            amount + getAmountAvailable(exhibitor), 0), [getAmountAvailable, exhibitors]);

    return (
        <HomeLayout>
            <Container>
                <Header />
                <TotalBalance totalAmountAvailable={getTotalAmountAvailable()} />
                <Carousel>
                    <Items
                        ref={ref}
                        $length={exhibitors.length}
                    >
                        {exhibitors.map((exhibitor, index) => (
                            <Item
                                id={`home.exhibitors${index}.card`}
                                key={index}
                                $active={index === selectedIndex}
                                $first={index === 0}
                                $last={index === exhibitors.length - 1}
                                onTouchStart={() => handleNextItem(index + 1)}
                                onClick={() => handleNextItem(index + 1)}
                            >
                                <ExhibitorLogo
                                    logo={exhibitor.logo}
                                    name={exhibitor.name}
                                />
                                <ExhibitorBalance
                                    available={getAmountAvailable(exhibitor)}
                                    index={index}
                                />
                            </Item>
                        ))}
                    </Items>
                    <div>
                        {exhibitors.map((_, index) => (
                            <ItemShortcut
                                id={`home.exhibitors${index}.shortcut`}
                                key={index}
                                $active={index === selectedIndex}
                                onClick={() => handleNextItem(index + 1)}
                            />
                        ))}
                    </div>
                </Carousel>
                {selectedExhibitor && (
                    <ExhibitorVouchersType
                        exhibitor={selectedExhibitor}
                        voucherTypes={selectedExhibitor.voucherTypes}
                    />
                )}
            </Container>
        </HomeLayout>
    );
};

export { HomePage };
